import { AssignmentClinicianDecisionSupport } from '@/components/decisionSupport/AssignmentClinicianDecisionSupport';
import { AssignmentDateDecisionSupport } from '@/components/decisionSupport/AssignmentDateDecisionSupport';
import {
    UseAssignmentVisitClinicianApplyResult,
    UseAssignmentVisitDateApplyResult,
} from '@/components/decisionSupport/hooks/useAssignmentVisitApply';
import {
    DecisionSupportSelection,
    canSelectVisit,
    pickTopRanked,
    selectionCaption,
} from '@/components/decisionSupport/utils/decisionSupport.utils';
import { CaptionLabel } from '@/components/labels/CaptionLabel';
import { NoneLabel } from '@/components/labels/NoneLabel';
import { HorizontalStack, VerticalStack } from '@/components/utils/linearLayouts';
import { GraphInputHelpers } from '@/fetching/graphql/GraphInputHelpers';
import { coerceToDate } from '@/utils/datetimes/javascriptDates';
import { Checkbox } from '@mui/material';
import { FC, useMemo } from 'react';

// New DS visitAssignment cells for UnifiedVisitsWorkflowTable visit rows
// (FE-577). Prop-based (the table owns the apply hooks); VisitRequest rows keep
// using the legacy cells.

type ClinicianCellProps = {
    visitId: string;
    appointments: { id: string }[];
    apply: UseAssignmentVisitClinicianApplyResult;
    disabled?: boolean;
};

export const AssignmentClinicianVisitCell: FC<ClinicianCellProps> = ({
    visitId,
    appointments,
    apply,
    disabled = false,
}) => {
    const { definitions, clinicianRecommendation } = apply.getRec(visitId);
    const recommendations = useMemo(
        () => clinicianRecommendation?.recommendations ?? [],
        [clinicianRecommendation],
    );
    const hasRecommendations = recommendations.length > 0;
    const topRecommendation = useMemo(() => pickTopRanked(recommendations), [recommendations]);
    const isSelected = apply.isSelected(visitId);
    const hasExplicitPick = appointments.some(
        (appointment) => apply.getSelectedClinician(visitId, appointment.id) != null,
    );

    const controlsDisabled = apply.loading || apply.submitting || disabled;

    return (
        <HorizontalStack
            justifyContent='space-between'
            onClick={(event) => event.stopPropagation()}
        >
            <VerticalStack>
                {appointments.length > 0 ? (
                    appointments.map((appointment) => {
                        const pickedId = apply.getSelectedClinician(visitId, appointment.id);
                        const resolvedClinicianId =
                            pickedId ?? topRecommendation?.clinician.id ?? null;
                        const selectedRec =
                            resolvedClinicianId == null
                                ? null
                                : [
                                      ...recommendations,
                                      ...(clinicianRecommendation?.incompatibleClinicians ?? []),
                                  ].find((r) => r.clinician.id === resolvedClinicianId) ?? null;
                        const selection: DecisionSupportSelection | null = selectedRec
                            ? {
                                  id: selectedRec.clinician.id,
                                  label: selectedRec.clinician.fullName,
                              }
                            : null;
                        return (
                            <VerticalStack key={appointment.id}>
                                <CaptionLabel fontSize={'0.8em'}>
                                    {selectionCaption(!apply.loading && pickedId != null)}
                                </CaptionLabel>
                                <HorizontalStack spacing={1} alignItems='center'>
                                    {apply.loading ? (
                                        <NoneLabel text={'Loading...'} fontSize={'14px'} />
                                    ) : selection ? (
                                        selection.label
                                    ) : (
                                        <NoneLabel text={'None'} fontSize={'14px'} />
                                    )}
                                    <AssignmentClinicianDecisionSupport
                                        definitions={definitions}
                                        clinicianRecommendation={clinicianRecommendation}
                                        selectedClinician={selection}
                                        setSelectedClinician={(next) =>
                                            apply.setSelectedClinician(
                                                visitId,
                                                appointment.id,
                                                next?.id ?? null,
                                            )
                                        }
                                        disabled={controlsDisabled}
                                    />
                                </HorizontalStack>
                            </VerticalStack>
                        );
                    })
                ) : (
                    <NoneLabel />
                )}
            </VerticalStack>
            {canSelectVisit(hasRecommendations, hasExplicitPick) && (
                <Checkbox
                    size='small'
                    disabled={controlsDisabled}
                    checked={isSelected}
                    onChange={(event) => {
                        event.stopPropagation();
                        apply.setSelected(visitId, !isSelected);
                    }}
                />
            )}
        </HorizontalStack>
    );
};

type DateCellProps = {
    visitId: string;
    apply: UseAssignmentVisitDateApplyResult;
    disabled?: boolean;
};

export const AssignmentDateVisitCell: FC<DateCellProps> = ({
    visitId,
    apply,
    disabled = false,
}) => {
    const { definitions, dateRecommendation } = apply.getRec(visitId);
    const recommendations = useMemo(
        () => dateRecommendation?.recommendations ?? [],
        [dateRecommendation],
    );
    const hasRecommendations = recommendations.length > 0;
    const isSelected = apply.isSelected(visitId);
    const pickedDate = apply.getSelectedDate(visitId);

    const topRecommendation = useMemo(() => pickTopRanked(recommendations), [recommendations]);

    const resolvedDate =
        pickedDate ?? (topRecommendation ? GraphInputHelpers.date(topRecommendation.date) : null);

    const selectedRow = useMemo(() => {
        if (!resolvedDate) {
            return null;
        }
        const allDates: { date: Date }[] = [
            ...recommendations,
            ...(dateRecommendation?.incompatibleDates ?? []),
        ];
        return allDates.find((r) => GraphInputHelpers.date(r.date) === resolvedDate) ?? null;
    }, [recommendations, dateRecommendation, resolvedDate]);

    const selection: DecisionSupportSelection | null = selectedRow
        ? {
              id: GraphInputHelpers.date(selectedRow.date),
              label: coerceToDate(selectedRow.date).toDateString(),
          }
        : null;

    const controlsDisabled = apply.loading || apply.submitting || disabled;

    return (
        <HorizontalStack
            justifyContent='space-between'
            onClick={(event) => event.stopPropagation()}
        >
            <VerticalStack>
                <CaptionLabel fontSize={'0.8em'}>
                    {selectionCaption(pickedDate != null)}
                </CaptionLabel>
                <HorizontalStack spacing={1} alignItems='center'>
                    {selectedRow ? (
                        coerceToDate(selectedRow.date).toDateString()
                    ) : (
                        <NoneLabel text={apply.loading ? 'Loading...' : 'None'} fontSize={'14px'} />
                    )}
                    <AssignmentDateDecisionSupport
                        definitions={definitions}
                        dateRecommendation={dateRecommendation}
                        selectedDate={selection}
                        setSelectedDate={(next) => apply.setSelectedDate(visitId, next?.id ?? null)}
                        disabled={controlsDisabled}
                    />
                </HorizontalStack>
            </VerticalStack>
            {canSelectVisit(hasRecommendations, pickedDate != null) && (
                <Checkbox
                    size='small'
                    disabled={controlsDisabled}
                    checked={isSelected}
                    onChange={(event) => {
                        event.stopPropagation();
                        apply.setSelected(visitId, !isSelected);
                    }}
                />
            )}
        </HorizontalStack>
    );
};
